/**
 * AppointmentFieldsAdmin — which identifying numbers a booking must carry (#131).
 *
 * ⭐ Pete, 2026-08-20: *"we should allow appointments to be created without a PO /
 * Receipt Number required - or atleast make this configurable (in a config screen)
 * for the carrier/client screens and the internal dock scheduler screen"* — and, on
 * granularity, *"It should be granular allow each field to be determined if
 * required or not."*
 *
 * ⛔ THIS IS NOT THE DRIVER CHECK-IN SCREEN, and the difference is worth saying out
 * loud because the two use the same nouns. `dock.CheckInConfig` has
 * `CollectPoNumber` / `CollectContainerNumber` / `CollectTrailerNumber`: that is
 * WHAT THE KIOSK ASKS A DRIVER FOR at the gate. This is WHAT A BOOKING MUST CARRY,
 * hours or days earlier, from one of three kinds of user. A facility will genuinely
 * want the kiosk to ASK for a container number while not REQUIRING one on the
 * booking — because not knowing it yet is the entire premise of the feature.
 *
 * ⚠️ NOTHING IS REQUIRED BY DEFAULT. A facility with no rows behaves exactly as it
 * does the day this ships: nothing required, and a warning when nothing at all
 * identifies the load.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, AlertTriangle, Info, Loader2, RotateCcw, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    ApiCallError, getFieldPolicy, saveFieldPolicy, resetFieldPolicy,
    type FieldPolicyResponse, type FieldPolicyRowDto, type BookingScope,
} from '@/api/client';

interface Props {
    getToken: () => Promise<string>;
    facilities: { FacilityCode: string; DisplayName: string }[];
    /**
     * ⭐ THE FACILITY THE CALENDAR IS SHOWING. See DocumentConfigAdmin for what
     * defaulting to facilities[0] cost on 2026-08-25: settings saved against a
     * facility the user was not looking at, and no screen able to say so.
     */
    initialFacilityCode?: string | null;
}

type PolicyDirection = 'INBOUND' | 'OUTBOUND' | 'BOTH';

/**
 * ⭐ SCOPES ARE EXPLAINED, NOT JUST LISTED. "Carrier" and "Scheduler" are obvious
 * to whoever built this and not at all obvious to whoever configures it six months
 * later — and getting them the wrong way round means a carrier is held to Symbia's
 * internal standard, or nobody is held to anything.
 */
const SCOPE_HELP: Record<BookingScope, { title: string; blurb: string }> = {
    Scheduler: {
        title: 'Symbia scheduler',
        blurb: 'Your own people booking from the dock scheduler screen. They have the '
             + 'WMS open and can look a number up — but they are also the ones taking '
             + 'a phone call about twenty trucks with no paperwork yet.',
    },
    Carrier: {
        title: 'Carrier portal',
        blurb: 'A carrier booking themselves in, often out of hours with nobody here '
             + 'to judge. ⚠️ Today they can already book with no PO at all — this is '
             + 'the first place that can be tightened.',
    },
    Client: {
        title: 'Client portal',
        blurb: 'Not built yet (#39). Set it now if you like; nothing reads it until '
             + 'there is a client portal to read it.',
    },
};

const DIRECTIONS: { value: PolicyDirection; label: string }[] = [
    { value: 'BOTH', label: 'Inbound and outbound' },
    { value: 'INBOUND', label: 'Inbound only' },
    { value: 'OUTBOUND', label: 'Outbound only' },
];

type Flags = Pick<FieldPolicyRowDto,
    'requirePoNumber' | 'requireReferenceNumber' | 'requireContainerNumber'
    | 'requireTrailerNumber' | 'warnWhenNoReference'>;

const FLAG_FOR: Record<string, keyof Flags> = {
    poNumber: 'requirePoNumber',
    referenceNumber: 'requireReferenceNumber',
    containerNumber: 'requireContainerNumber',
    trailerNumber: 'requireTrailerNumber',
};

export function AppointmentFieldsAdmin({ getToken, facilities, initialFacilityCode }: Props) {
    const [facilityCode, setFacilityCode] = useState(
        (initialFacilityCode && facilities.some(f => f.FacilityCode === initialFacilityCode)
            ? initialFacilityCode
            : facilities[0]?.FacilityCode) ?? '',
    );
    const [scope, setScope] = useState<BookingScope>('Scheduler');
    const [direction, setDirection] = useState<PolicyDirection>('BOTH');

    const [data, setData] = useState<FieldPolicyResponse | null>(null);
    const [flags, setFlags] = useState<Flags | null>(null);
    /** null = this scope/direction has no row, so it is on the shipped defaults. */
    const [hasRow, setHasRow] = useState(false);

    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [note, setNote] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!facilityCode) return;
        setLoading(true);
        setError(null);
        try {
            const r = await getFieldPolicy(await getToken(), facilityCode);
            setData(r);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : String(err));
        } finally {
            setLoading(false);
        }
    }, [facilityCode, getToken]);

    useEffect(() => { void load(); }, [load]);

    // Pick out the row for the selected scope+direction, or fall back to the
    // shipped defaults so the checkboxes show what would actually happen.
    useEffect(() => {
        if (!data) return;
        const row = data.rows.find(r => r.scope === scope && r.direction === direction);
        setHasRow(!!row);
        setFlags(row ? {
            requirePoNumber: row.requirePoNumber,
            requireReferenceNumber: row.requireReferenceNumber,
            requireContainerNumber: row.requireContainerNumber,
            requireTrailerNumber: row.requireTrailerNumber,
            warnWhenNoReference: row.warnWhenNoReference,
        } : { ...data.shippedDefaults });
        setNote(null);
    }, [data, scope, direction]);

    async function save() {
        if (!flags) return;
        setBusy(true); setError(null); setNote(null);
        try {
            await saveFieldPolicy(await getToken(), { facilityCode, scope, direction, ...flags });
            setNote('Saved.');
            await load();
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : String(err));
        } finally {
            setBusy(false);
        }
    }

    async function reset() {
        setBusy(true); setError(null); setNote(null);
        try {
            const r = await resetFieldPolicy(await getToken(), { facilityCode, scope, direction });
            setNote(r.message);
            await load();
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : String(err));
        } finally {
            setBusy(false);
        }
    }

    const fields = data?.fields ?? [];
    const anyRequired = flags
        ? Object.entries(FLAG_FOR).some(([, k]) => flags[k] === true)
        : false;

    return (
        <div className="space-y-4">
            <div>
                <h3 className="text-base font-semibold">Appointment fields</h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                    Which identifying numbers a booking must carry. Set separately for
                    each booking screen, because they are used by different people in
                    different situations.
                </p>
            </div>

            {/* ⛔ THE MIGRATION WARNING COMES FIRST. A screen that renders happily
                against a missing table would have somebody set a policy, see it
                accepted and discover months later that it was never stored. */}
            {data && !data.migrationApplied && (
                <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                    <AlertCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
                    <div>
                        <div className="font-medium">Migration 151 has not been applied</div>
                        <div className="mt-0.5 text-muted-foreground">
                            Nothing can be saved and no requirement is being enforced —
                            every facility is on the shipped defaults. Run
                            <code className="mx-1">151_appointment_field_policy.sql</code>
                            and reopen this screen.
                        </div>
                    </div>
                </div>
            )}

            {error && (
                <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                    <AlertCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
                    <span>{error}</span>
                </div>
            )}

            {/* Requirements already saved that no booking form can satisfy. Normally
                empty — but every policy is set by hand until this screen ships. */}
            {data && data.unsatisfiable.length > 0 && (
                <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                    <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
                    <div>
                        <div className="font-medium">
                            Some saved requirements cannot be met by any booking form
                        </div>
                        <ul className="mt-1 space-y-0.5 text-muted-foreground">
                            {data.unsatisfiable.map((u, i) => (
                                <li key={i}>
                                    {u.scope} · {u.direction} requires a{' '}
                                    {fields.find(f => f.key === u.field)?.label ?? u.field},
                                    which no booking screen collects — every appointment
                                    would be refused.
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                    <Label>Facility</Label>
                    <Select value={facilityCode} onValueChange={setFacilityCode}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {facilities.map(f => (
                                <SelectItem key={f.FacilityCode} value={f.FacilityCode}>
                                    {f.DisplayName || f.FacilityCode}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1.5">
                    <Label>Booking screen</Label>
                    <Select value={scope} onValueChange={(v) => setScope(v as BookingScope)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {(data?.scopes ?? ['Scheduler', 'Carrier', 'Client']).map(s => (
                                <SelectItem key={s} value={s}>{SCOPE_HELP[s].title}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1.5">
                    <Label>Applies to</Label>
                    <Select value={direction} onValueChange={(v) => setDirection(v as PolicyDirection)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {DIRECTIONS.map(d => (
                                <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="flex gap-2 rounded-md border bg-muted/40 p-3 text-sm">
                <Info className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                <span className="text-muted-foreground">{SCOPE_HELP[scope].blurb}</span>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
            ) : flags && (
                <div className="space-y-3">
                    {/* ⭐ SAYS WHETHER THIS IS A CHOICE OR A DEFAULT. Four unchecked
                        boxes look identical either way, and "(defaults)" is the
                        difference between "nobody has set this" and "somebody
                        decided nothing is required". */}
                    <div className="text-xs text-muted-foreground">
                        {hasRow
                            ? `Configured for ${SCOPE_HELP[scope].title} · ${DIRECTIONS.find(d => d.value === direction)?.label}.`
                            : 'No rule set — showing the shipped defaults. Saving creates one.'}
                    </div>

                    <div className="rounded-md border divide-y">
                        {fields.map(f => {
                            const key = FLAG_FOR[f.key];
                            const disabled = !f.bookable;
                            return (
                                <label
                                    key={f.key}
                                    className={`flex items-start gap-3 p-3 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
                                >
                                    <input
                                        type="checkbox"
                                        className="mt-0.5"
                                        checked={flags[key] === true}
                                        // ⛔ DISABLED WHERE NO FORM CAN SUPPLY THE FIELD.
                                        // Requiring a trailer number would refuse every
                                        // booking, because a driver arrives with it — the
                                        // server refuses to save it too, but a checkbox
                                        // that can be ticked and then rejected is a worse
                                        // screen than one that explains itself.
                                        disabled={disabled || busy}
                                        onChange={(e) =>
                                            setFlags({ ...flags, [key]: e.target.checked })}
                                    />
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium">
                                            Require {f.label}
                                        </div>
                                        {disabled && (
                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                No booking screen collects this — it is filled
                                                in from the driver's paperwork when a driver is
                                                matched to the appointment.
                                            </div>
                                        )}
                                    </div>
                                </label>
                            );
                        })}
                    </div>

                    <label className="flex items-start gap-3 rounded-md border p-3 cursor-pointer">
                        <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={flags.warnWhenNoReference === true}
                            disabled={busy}
                            onChange={(e) =>
                                setFlags({ ...flags, warnWhenNoReference: e.target.checked })}
                        />
                        <div>
                            <div className="text-sm font-medium">
                                Warn when nothing identifies the appointment
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                                Shows a note — never blocks — when a booking has no PO,
                                reference, container or trailer number at all. ⚠️ Mass-created
                                appointments have none of these by design, so this is advice,
                                not a refusal.
                            </div>
                        </div>
                    </label>

                    {!anyRequired && (
                        <p className="text-xs text-muted-foreground">
                            Nothing is required for this screen — a booking can be created
                            with no numbers at all and matched to the driver's paperwork when
                            the truck arrives.
                        </p>
                    )}

                    {note && (
                        <div className="rounded-md border bg-muted/40 p-2 text-sm">{note}</div>
                    )}

                    <div className="flex items-center justify-between gap-2">
                        <Button
                            variant="outline" size="sm" onClick={() => void reset()}
                            disabled={busy || !hasRow || !data?.migrationApplied}
                            title={hasRow ? 'Delete this rule and go back to the shipped defaults'
                                          : 'Already on the shipped defaults'}
                        >
                            <RotateCcw className="h-3.5 w-3.5" />
                            Back to defaults
                        </Button>
                        <Button
                            size="sm" onClick={() => void save()}
                            disabled={busy || !data?.migrationApplied}
                        >
                            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  : <Save className="h-3.5 w-3.5" />}
                            Save
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
